import {
  LiveEventsCountQuery,
  LiveEventsCountQueryVariables,
} from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { FC } from 'types';
import { goToNewEvent } from 'utils/routerHelpers';
import { genericToastAlert } from 'utils/toastHelpers';
import { canCreateEvent } from 'utils/userHelpers';

import React from 'react';

import { useQuery } from '@apollo/client';

import { Button } from '@thinkific/chalk/Button';
import { Plus } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import CommunityHeader from 'components/CommunityHeader/CommunityHeader';
import EventListPage from 'components/Events/components/EventListPage/EventListPage';
import EventsSkeleton from 'components/Events/components/EventsSkeleton/EventsSkeleton';
import { getLayout } from 'components/Layouts/MainLayout';
import Layout from 'components/new/layout';

import { LIVE_EVENTS_COUNT_QUERY } from 'schema/Event/queries';

export const EventsPage: FC = () => {
  const { community, trackEvent, currentUser } = useCommunityContext();
  const { t } = useTranslation();
  const router = useRouter();

  const { loading, previousData } = useQuery<LiveEventsCountQuery, LiveEventsCountQueryVariables>(
    LIVE_EVENTS_COUNT_QUERY,
    {
      variables: {
        eventSourceId: community.id,
      },
      fetchPolicy: 'cache-first',
      onError: () => {
        genericToastAlert();
      },
    }
  );

  const goToNewEventPage = (e) => {
    e.preventDefault();
    trackEvent(TrackingEvent.CLICK_CREATE_LIVE_EVENT, {
      [TrackingProperty.COMPONENT]: 'EventsPage',
    });

    goToNewEvent(router, community.id);
  };

  const head = (
    <>
      <CommunityHeader pageTitle={t('pages-events_title', 'Events')} />
      <Layout.ContextHeader
        action={
          canCreateEvent(currentUser) && (
            <Button
              data-qa="main-search_button"
              emphasis="low"
              href={`/communities/${community.id}/events/new/`}
              icon={Plus}
              hiddenLabel
              onClick={goToNewEventPage}
            >
              {t('shared-CREATE_EVENT', 'CREATE EVENT')}
            </Button>
          )
        }
        pageTitle={t('pages-events_title', 'Events')}
      />
    </>
  );

  if (loading && !previousData) {
    return (
      <>
        {head}
        <Layout.PageContent>
          <EventsSkeleton />
        </Layout.PageContent>
      </>
    );
  }

  return (
    <>
      {head}
      <Layout.PageContent>
        <EventListPage />
      </Layout.PageContent>
    </>
  );
};

EventsPage.getLayout = getLayout;

export default EventsPage;
